import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ChevronRight, User, Mail, Phone, MapPin, Building2,
  BadgeCheck, Settings, Zap, Handshake, Download, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { getAttribution } from "@/lib/track";
import { useSeo } from "@/hooks/useSeo";
import { Icon } from "@/lib/icon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";
import { Brands } from "@/pages/public/Home";
import { useLead } from "@/components/public/LeadDialog";

const INDICATOR_ICONS = [BadgeCheck, Settings, Zap, Handshake];

const fade = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

export default function PropertyOwners() {
  useSeo("/property-owners");
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState({});
  const [brands, setBrands] = useState([]);
  const [nf, setNf] = useState(false);

  useEffect(() => {
    api.get("/public/property-owners").then((r) => setPage(r.data)).catch(() => setNf(true));
    api.get("/public/homepage").then((r) => { setSettings(r.data.settings); setBrands(r.data.brands); });
  }, []);

  if (nf) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-50">
        <h1 className="font-display text-3xl font-bold text-slate-900">Page not available</h1>
        <Link to="/" className="text-primary font-semibold">← Back to home</Link>
      </div>
    );
  }
  if (!page) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="bg-white" data-testid="property-owners-page">
      <Header phone={settings.phone} />
      <Hero hero={page.hero} formCfg={page.form} types={page.property_types.items} />
      <WhyPartner why={page.why} />
      <ProcessAndCta process={page.process} cta={page.cta} />
      <PropertyTypes cfg={page.property_types} />
      <Brands brands={brands} />
      <Footer settings={settings} />
    </div>
  );
}

/* ---------------- Hero + floating enquiry card ---------------- */
function Hero({ hero, formCfg, types }) {
  const { openLead } = useLead();
  const scrollToForm = () => document.getElementById("po-form-card")?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <section className="relative">
      <div className="absolute inset-0">
        <img src={hero.image} alt={hero.image_alt} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/25" />
      </div>

      <div className="relative container-cz grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-6 py-14 lg:py-20 items-start">
        <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="pt-2 lg:pr-16">
          <p className="eyebrow mb-4">{hero.eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02] text-slate-900">
            {hero.title_line1}<br />
            <span className="text-primary">{hero.title_line2}</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl">{hero.description}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button data-testid="po-partner-cta" onClick={scrollToForm} className="rounded-full h-12 px-7 gap-2 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
              {hero.primary_cta?.label || "Partner With Us"} <ArrowRight className="w-4 h-4" />
            </Button>
            <Button data-testid="po-talk-team" onClick={() => openLead("enterprise")} variant="outline" className="rounded-full h-12 px-7 border-slate-300 bg-white/70 backdrop-blur">
              {hero.secondary_cta?.label || "Talk to Our Team"}
            </Button>
          </div>
          <div className="flex flex-wrap gap-x-7 gap-y-3 mt-9">
            {(hero.indicators || []).map((ind, i) => {
              const I = INDICATOR_ICONS[i % INDICATOR_ICONS.length];
              return (
                <span key={ind} className="flex items-center gap-2 text-[13px] font-medium text-slate-700" data-testid={`po-indicator-${i}`}>
                  <I className="w-4 h-4 text-primary" /> {ind}
                </span>
              );
            })}
          </div>
        </motion.div>

        <motion.div id="po-form-card" initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
          className="card-cz shadow-cz p-6 sm:p-7 scroll-mt-24">
          <EnquiryForm formCfg={formCfg} types={types} />
        </motion.div>
      </div>
    </section>
  );
}

function EnquiryForm({ formCfg, types }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", property_type: "", location: "", area: "", message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Full name is required";
    if (!form.email.trim()) er.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) er.email = "Enter a valid email";
    if (!form.phone.trim()) er.phone = "Phone number is required";
    if (!form.city.trim()) er.city = "City is required";
    if (!form.property_type) er.property_type = "Select a property type";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Please fill the required fields"); return; }
    setBusy(true);
    try {
      await api.post("/public/property-enquiries", { ...form, ...getAttribution() });
      setDone(true);
      toast.success("Enquiry submitted! Our team will reach out within 24 hours.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-12" data-testid="po-success">
        <span className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4"><BadgeCheck className="w-7 h-7" /></span>
        <h3 className="font-display text-xl font-bold text-slate-900">Thank you!</h3>
        <p className="text-sm text-slate-500 mt-2">Your property enquiry has been received. Our team will get in touch within 24 hours.</p>
      </div>
    );
  }

  const F = ({ k, ph, icon: I, req, type = "text" }) => (
    <div>
      <div className="relative">
        <I className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input type={type} placeholder={ph} value={form[k]} onChange={set(k)}
          className={`h-11 pl-10 rounded-lg ${errors[k] ? "border-rose-400" : ""}`} data-testid={`po-field-${k}`} />
      </div>
      {errors[k] && <p className="text-xs text-rose-600 mt-1">{errors[k]}</p>}
    </div>
  );

  return (
    <div>
      <h2 className="font-display text-xl font-bold text-slate-900">{formCfg?.title || "Tell Us About Your Property"}</h2>
      <p className="text-sm text-slate-500 mt-1 mb-5">{formCfg?.subtitle || "Our team will get in touch within 24 hours."}</p>
      <form onSubmit={submit} className="space-y-3">
        <F k="name" ph="Full Name*" icon={User} />
        <F k="email" ph="Email Address*" icon={Mail} type="email" />
        <F k="phone" ph="Phone Number*" icon={Phone} />
        <F k="city" ph="City*" icon={MapPin} />
        <div>
          <div className="relative">
            <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10 pointer-events-none" />
            <Select value={form.property_type} onValueChange={set("property_type")}>
              <SelectTrigger className={`h-11 pl-10 rounded-lg ${errors.property_type ? "border-rose-400" : ""}`} data-testid="po-field-property-type">
                <SelectValue placeholder="Property Type*" />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.title} value={t.title}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {errors.property_type && <p className="text-xs text-rose-600 mt-1">{errors.property_type}</p>}
        </div>
        <F k="location" ph="Property Location" icon={MapPin} />
        <div>
          <Input placeholder="Approx. Available Area (Sq. Ft.)" value={form.area} onChange={set("area")} className="h-11 rounded-lg" data-testid="po-field-area" />
        </div>
        <Textarea placeholder="Message / Additional Details" value={form.message} onChange={set("message")} className="rounded-lg" data-testid="po-field-message" />
        <Button type="submit" disabled={busy} data-testid="po-submit" className="w-full h-12 rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 font-bold gap-2">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <>{formCfg?.cta || "Submit Enquiry"} <ArrowRight className="w-4 h-4" /></>}
        </Button>
      </form>
    </div>
  );
}

/* ---------------- Why partner ---------------- */
function WhyPartner({ why }) {
  return (
    <section className="py-16 lg:py-20">
      <div className="container-cz">
        <div className="grid lg:grid-cols-2 gap-10 items-end">
          <motion.div {...fade}>
            <p className="eyebrow mb-3">{why.eyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
              {why.title_line1}<br />{why.title_line2}
            </h2>
            <p className="text-slate-600 mt-4 max-w-md">{why.description}</p>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-5">
            {why.benefits.slice(0, 3).map((b, i) => <BenefitCard key={b.title} b={b} i={i} />)}
          </div>
        </div>
        {why.benefits.length > 3 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5 lg:max-w-3xl lg:ml-auto lg:justify-end">
            {why.benefits.slice(3).map((b, i) => <BenefitCard key={b.title} b={b} i={i + 3} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function BenefitCard({ b, i }) {
  return (
    <motion.div {...fade} transition={{ ...fade.transition, delay: i * 0.06 }}
      className="flex items-start gap-3" data-testid={`po-benefit-${i}`}>
      <span className="w-10 h-10 rounded-lg bg-sky-50 text-primary flex items-center justify-center shrink-0 border border-sky-100">
        <Icon name={b.icon} className="w-5 h-5" />
      </span>
      <div>
        <h3 className="font-semibold text-slate-900 text-sm leading-snug">{b.title}</h3>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{b.description}</p>
      </div>
    </motion.div>
  );
}

/* ---------------- Process + CTA panel ---------------- */
function ProcessAndCta({ process, cta }) {
  const { openLead } = useLead();
  const scrollToForm = () => document.getElementById("po-form-card")?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <section className="py-14 border-t border-slate-100">
      <div className="container-cz grid lg:grid-cols-[1.5fr_1fr] gap-10 items-start">
        <div>
          <p className="eyebrow mb-3">{process.eyebrow}</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-9">{process.title}</h2>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {process.steps.map((s, i) => (
              <motion.div key={s.title} {...fade} transition={{ ...fade.transition, delay: i * 0.07 }} className="relative" data-testid={`po-step-${i}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-800 font-display font-bold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="w-12 h-12 rounded-xl bg-sky-50 border border-sky-100 text-primary flex items-center justify-center"><Icon name={s.icon} className="w-6 h-6" /></span>
                  {i < process.steps.length - 1 && <ArrowRight className="w-5 h-5 text-primary/50 hidden xl:block ml-auto" />}
                </div>
                <h3 className="font-semibold text-slate-900 text-sm">{s.title}</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div {...fade} className="rounded-2xl bg-amber-50 border border-amber-100 p-7" data-testid="po-cta-panel">
          <p className="text-[11px] font-bold tracking-[0.16em] uppercase text-slate-500">{cta.eyebrow}</p>
          <h3 className="font-display text-2xl font-bold text-slate-900 mt-2 leading-snug">{cta.title}</h3>
          <div className="space-y-3 mt-6">
            <Button onClick={scrollToForm} data-testid="po-cta-partner" className="w-full h-12 rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 font-bold gap-2">
              {cta.primary_label || "Partner With Us"} <ArrowRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" data-testid="po-cta-brochure"
              onClick={() => cta.brochure_url ? window.open(cta.brochure_url, "_blank") : toast.info("Brochure will be shared shortly after your enquiry.")}
              className="w-full h-12 rounded-lg bg-white border-slate-300 font-semibold gap-2">
              <Download className="w-4 h-4" /> {cta.brochure_label || "Download Brochure"}
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Property types ---------------- */
function PropertyTypes({ cfg }) {
  const scrollToForm = () => document.getElementById("po-form-card")?.scrollIntoView({ behavior: "smooth", block: "center" });
  return (
    <section className="py-14 border-t border-slate-100">
      <div className="container-cz">
        <p className="eyebrow mb-8">{cfg.eyebrow}</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {cfg.items.map((t, i) => (
            <motion.button key={t.title} {...fade} transition={{ ...fade.transition, delay: i * 0.05 }}
              onClick={scrollToForm} data-testid={`po-type-${i}`}
              className="group relative rounded-2xl overflow-hidden aspect-[16/11] text-left">
              <img src={t.image} alt={t.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/20 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-4 flex items-end justify-between gap-2">
                <h3 className="text-white font-semibold text-sm sm:text-base leading-snug">{t.title}</h3>
                <span className="w-8 h-8 rounded-full bg-white text-slate-900 flex items-center justify-center shrink-0 group-hover:bg-accent transition-colors">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
